import React, { useEffect, useState } from "react";
import api, { STAGE_LABELS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { TrendingUp, FolderKanban, CheckCircle2, Clock, AlertTriangle, Users, FileText, Bell, Mail, IndianRupee, DollarSign, ArrowUp, ArrowDown, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import SurveydraftsActivityPanel from "@/components/dashboard/SurveydraftsActivityPanel";
import EmployeeDashboard from "@/pages/EmployeeDashboard";
import { KpiLine } from "./StatTiles";

export function OutstandingTile({ data }) {
  const SYM = { USD: "$", INR: "₹", EUR: "€", GBP: "£" };
  const curs = Object.entries(data.by_currency || {}).sort((a, b) => b[1].outstanding - a[1].outstanding);
  const hasDebt = curs.length > 0;
  return (
    <div className="brut-card p-5" data-testid="outstanding-widget">
      <div className="flex items-end justify-between mb-3">
        <div>
          <div className="label-mono flex items-center gap-1.5">
            <IndianRupee className="w-3 h-3" /> OUTSTANDING BALANCES
          </div>
          <div className="font-heading text-lg font-bold">{data.total_unpaid_invoices} unpaid invoice{data.total_unpaid_invoices === 1 ? "" : "s"}</div>
        </div>
        <span className={`label-mono font-bold border border-zinc-900 px-2 py-0.5 ${hasDebt ? "bg-red-200" : "bg-green-200"}`} data-testid="outstanding-status-pill">
          {hasDebt ? "DUE" : "CLEAR"}
        </span>
      </div>
      <div className={`border border-zinc-900 p-4 mb-3 ${hasDebt ? "bg-red-50" : "bg-green-50"}`} data-testid="outstanding-totals">
        <div className="label-mono">TOTAL OUTSTANDING</div>
        {hasDebt ? curs.map(([cur, v]) => (
          <div key={cur}>
            <div className="flex items-baseline justify-between">
              <div className="font-heading text-3xl font-black mt-1 text-red-800">{SYM[cur] || ""}{v.outstanding.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
              <div className="text-[11px] font-mono text-zinc-600">{cur} · {v.invoice_count} inv</div>
            </div>
            {v.aging && (
              <div className="flex gap-1 mt-1.5" data-testid={`aging-${cur}`}>
                {[["d0_30", "0-30", "bg-amber-200"], ["d31_60", "31-60", "bg-orange-300"], ["d61_90", "61-90", "bg-red-300"], ["d90_plus", "90+", "bg-red-500 text-white"]].map(([k, label, cls]) => (
                  v.aging[k] > 0 ? (
                    <div key={k} className={`flex-1 border border-zinc-900 px-1 py-0.5 text-center ${cls}`} title={`${label} days overdue`}>
                      <div className="font-mono text-[8px] uppercase font-bold">{label}d</div>
                      <div className="font-mono text-[10px] font-bold">{SYM[cur] || ""}{v.aging[k].toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
                    </div>
                  ) : null
                ))}
              </div>
            )}
          </div>
        )) : <div className="font-heading text-3xl font-black mt-1 text-green-800">0</div>}
      </div>
      {(data.top_debtors || []).length > 0 && (
        <div className="text-[11px] font-mono space-y-1" data-testid="top-debtors">
          <div className="label-mono">TOP DEBTORS</div>
          {data.top_debtors.map((d, i) => (
            <div key={i} className="flex justify-between border-b border-zinc-200 pb-0.5">
              <span className="truncate mr-2">{d.client_name}</span>
              <b className="text-red-700 whitespace-nowrap">{SYM[d.currency] || ""}{d.outstanding.toLocaleString(undefined, { maximumFractionDigits: 0 })}</b>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ----------------------------- Cash-flow Runway -----------------------------
export function CashRunwayTile({ data }) {
  const fmt = (n) => `$${Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
  const months = data.runway_months;
  const status = data.runway_status;
  const palette = {
    critical: { bg: "bg-red-200", label: "CRITICAL" },
    warning: { bg: "bg-yellow-200", label: "WARNING" },
    healthy: { bg: "bg-green-200", label: "HEALTHY" },
    unknown: { bg: "bg-zinc-200", label: "—" },
  }[status] || { bg: "bg-zinc-200", label: "—" };
  return (
    <div className="brut-card p-5" data-testid="cash-runway-widget">
      <div className="flex items-end justify-between mb-3">
        <div>
          <div className="label-mono flex items-center gap-1.5">
            <DollarSign className="w-3 h-3" /> CASH-FLOW RUNWAY
          </div>
          <div className="font-heading text-lg font-bold">USD · 3-month lookahead</div>
        </div>
        <span className={`label-mono font-bold border border-zinc-900 px-2 py-0.5 ${palette.bg}`} data-testid="runway-status-pill">{palette.label}</span>
      </div>
      <div className="border border-zinc-900 p-4 bg-yellow-50 mb-3" data-testid="runway-months">
        <div className="label-mono">RUNWAY</div>
        <div className="font-heading text-4xl font-black mt-1">
          {months !== null && months !== undefined ? `${months.toFixed(1)} mo` : "—"}
        </div>
        <div className="text-[11px] font-mono text-zinc-600 mt-1">
          (AR + 3 × next-mo forecast) ÷ monthly outflow
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 text-xs font-mono">
        <KpiLine label="Forecast next mo" value={fmt(data.inflow?.forecast_next_month_usd)} />
        <KpiLine label="AR outstanding" value={fmt(data.inflow?.ar_outstanding_usd)} good />
        <KpiLine label="Payroll (last mo)" value={fmt(data.outflow?.payroll_usd)} bad />
        <KpiLine label="Expenses (last mo)" value={fmt(data.outflow?.expenses_usd)} bad />
      </div>
    </div>
  );
}

export function StaleJobsTile({ data }) {
  const navigate = useNavigate();
  const top = (data.jobs || []).slice(0, 5);
  return (
    <div className="brut-card p-5" data-testid="stale-jobs-widget">
      <div className="flex items-end justify-between mb-3">
        <div>
          <div className="label-mono flex items-center gap-1.5">
            <AlertTriangle className="w-3 h-3" /> STUCK JOBS
          </div>
          <div className="font-heading text-lg font-bold">{data.count} flagged · longer than priority threshold</div>
        </div>
        <button
          onClick={() => navigate("/jobs?filter=stale")}
          className="brut-btn-secondary px-2 py-1 text-[10px] font-mono"
          data-testid="stale-jobs-view-all"
        >
          VIEW ALL →
        </button>
      </div>
      {top.length === 0 ? (
        <div className="text-center text-zinc-500 font-mono text-xs py-6">EVERYTHING MOVING ON TIME 🎉</div>
      ) : (
        <ul className="space-y-1">
          {top.map((j) => (
            <li key={j.id} className="flex items-center justify-between text-xs font-mono border-b border-zinc-100 py-1.5" data-testid={`stale-row-${j.id}`}>
              <span className="truncate flex-1 mr-2">
                <span className="font-bold">{j.project_name}</span>
                <span className="text-zinc-500"> · {j.job_number}</span>
              </span>
              <span className="font-bold text-red-700 whitespace-nowrap" title={`${j.priority} threshold ${j.threshold_days}d`}>
                {j.days_in_stage}d stuck
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


